import dayjs from "dayjs";
import PropTypes from "prop-types";

import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
} from "@/features/shared";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 10,
    fontFamily: "Helvetica",
  },
  header: {
    fontSize: 20,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
  sectionTitle: {
    fontSize: 13,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginTop: 16,
    marginBottom: 12,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#ececec",
    fontFamily: "Helvetica-Bold",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  cell: {
    padding: 4,
  },
  attributeHeader: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginTop: 8,
  },
  infoSection: {
    flexDirection: "row",
    marginVertical: 16,
  },
  infoItem: {
    flex: 1,
  },
  summarySection: {
    marginTop: 16,
    alignItems: "flex-end",
  },
  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
    minWidth: 200,
  },
});

export const PurchasePaymentPdf = ({ purchasePayment, user }) => {
  const totalAllocatedAmount = purchasePayment.purchaseInvoicePayments.reduce(
    (sum, payment) => sum + payment.allocatedAmount,
    0
  );

  const totalDiscount = purchasePayment.purchaseInvoicePayments.reduce(
    (sum, payment) => sum + payment.discount,
    0
  );

  const grandTotal = totalAllocatedAmount - totalDiscount;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 16,
          }}
        >
          <View>
            <Text style={styles.header}>Pembayaran Pembelian</Text>
            <Text
              style={{
                fontSize: 12,
                color: "#364F6B",
                fontFamily: "Helvetica-Bold",
                marginTop: 4,
              }}
            >
              {purchasePayment.referenceNumber}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                textAlign: "right",
              }}
            >
              Dicetak oleh: {user.name}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: "#364F6B",
                marginTop: 2,
                textAlign: "right",
              }}
            >
              Tanggal cetak: {formatDateTime(dayjs())}
            </Text>
          </View>
        </View>

        {/* Information Section */}
        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Pembeli</Text>
            <Text style={styles.attributeHeader}>
              {purchasePayment.buyer.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Penjual</Text>
            <Text style={styles.attributeHeader}>
              {purchasePayment.seller.name}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text>Tanggal Transaksi</Text>
            <Text style={styles.attributeHeader}>
              {formatDate(dayjs(purchasePayment.transactionDate))}
            </Text>
          </View>
        </View>

        <View style={styles.infoSection}>
          <View style={styles.infoItem}>
            <Text>Cash Bank</Text>
            <Text style={styles.attributeHeader}>
              {purchasePayment.outletChartOfAccount?.name ?? "-"}
            </Text>
          </View>
          <View style={[styles.infoItem, { flex: 2 }]}>
            <Text>Catatan</Text>
            <Text style={styles.attributeHeader}>
              {purchasePayment.note ?? "-"}
            </Text>
          </View>
        </View>

        {/* Invoice Payments Table */}
        <Text style={styles.sectionTitle}>Detail Pembayaran Faktur</Text>
        <View style={styles.tableHeader}>
          <Text style={[styles.cell, { flex: 3 }]}>No. Faktur #</Text>
          <Text style={[styles.cell, { flex: 2 }]}>Tanggal</Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Total Faktur
          </Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Terutang
          </Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Bayar
          </Text>
          <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
            Diskon
          </Text>
        </View>
        {purchasePayment.purchaseInvoicePayments.map((payment) => (
          <View style={styles.tableRow} key={payment.id}>
            <View style={[styles.cell, { flex: 3 }]}>
              <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 9 }}>
                {payment.purchaseInvoice.referenceNumber}
              </Text>
              <Text style={{ color: "#666", fontSize: 8, marginTop: 1 }}>
                {payment.purchaseInvoice.sellerReferenceNumber}
              </Text>
            </View>
            <Text style={[styles.cell, { flex: 2 }]}>
              {formatDate(dayjs(payment.purchaseInvoice.transactionDate))}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.purchaseInvoice.grandTotal)}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.outstandingAmount)}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.allocatedAmount)}
            </Text>
            <Text style={[styles.cell, { flex: 2, textAlign: "right" }]}>
              {formatNumber(payment.discount)}
            </Text>
          </View>
        ))}

        {/* Summary Section */}
        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            <Text>Total Dibayar:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(totalAllocatedAmount)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text>Total Diskon:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(totalDiscount)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>Grand Total:</Text>
            <Text style={{ fontFamily: "Helvetica-Bold" }}>
              {formatMoney(grandTotal)}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

PurchasePaymentPdf.propTypes = {
  purchasePayment: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
};
